import React, { useContext, useState } from "react";
import axios from "axios";
import { useAlert } from "react-alert";
import { useApiError } from "../../hooks/useApiError.js";
import { useQueryClient } from "@tanstack/react-query";

import { useMarketplaceAccounts } from "../../hooks/marketplaceAccounts";
import { Loading } from "../../components";
import { UserContext, useEntitlements } from "../../contexts";

// Base URL the user types into Chrono24's dealer dashboard. In local dev
// VITE_API_URL is unset (Vite proxies /api → staging-api) — show the
// staging host so reps don't have to mentally translate localhost paths.
const FEED_BASE = (() => {
  const env = (import.meta.env.VITE_API_URL || "").replace(/\/$/, "");
  if (env) return env;
  return "https://staging-api.retail-circle.io";
})();

const feedUrlFor = (account) => {
  if (account.marketplace !== "chrono24") return null;
  const suffix = account.slug === null ? "/chrono24-feed" : `/chrono24-feed/${account.slug}`;
  return `${FEED_BASE}${suffix}`;
};

const EMPTY_FORM = {
  marketplace: "chrono24",
  label: "",
  slug: "",
  currency: "GBP",
  is_enabled: false,
  base_url: "",
  api_key: "",
};

/**
 * WatchFinder (Mirakl) credentials + connection test for one account.
 * The stored API key is never returned by the API (write-only); the field
 * starts blank and a placeholder shows when a key is already saved. The
 * Show/Hide toggle reveals what the user types before submitting.
 */
function WatchFinderCreds({ account, onConnected }) {
  const alert = useAlert();
  const { showApiError } = useApiError();
  const [baseUrl, setBaseUrl] = useState(account.base_url || "");
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const connected = account.connection_status === "connected" && account.has_api_key;

  const onConnect = async () => {
    if (!baseUrl || !apiKey) {
      alert.show("Base URL and API key are both required.");
      return;
    }
    setConnecting(true);
    try {
      await axios.post(`/api/marketplace-accounts/${account.id}/connect`, {
        base_url: baseUrl,
        api_key: apiKey,
      });
      setApiKey("");
      setShowKey(false);
      onConnected?.();
    } catch (err) {
      showApiError(err, "test the connection");
    } finally {
      setConnecting(false);
    }
  };

  return (
    <div className="ma-wf-creds">
      <div className="ma-wf-creds__status">
        {connected ? (
          <span className="ma-wf-creds__ok">
            ✓ Connected{account.external_shop_name ? ` — ${account.external_shop_name}` : ""}
          </span>
        ) : (
          <span className="ma-wf-creds__off">Not connected</span>
        )}
      </div>
      <label>
        <div className="ma-eb-card__label">Base URL</div>
        <input
          type="text"
          value={baseUrl}
          placeholder="https://watchfinder.mirakl.net"
          onChange={(e) => setBaseUrl(e.target.value)}
        />
      </label>
      <label>
        <div className="ma-eb-card__label">API Key</div>
        <div className="ma-wf-creds__key">
          <input
            type={showKey ? "text" : "password"}
            value={apiKey}
            autoComplete="off"
            placeholder={connected ? "•••••••• stored — enter a new key to replace" : "Mirakl API key"}
            onChange={(e) => setApiKey(e.target.value)}
          />
          <button
            type="button"
            className="rc-btn rc-btn--small"
            onClick={() => setShowKey((s) => !s)}
            disabled={!apiKey}
            title={apiKey ? "" : "The stored key is write-only — type a new key to reveal it"}
          >
            {showKey ? "Hide" : "Show"}
          </button>
          <button
            type="button"
            className="rc-btn rc-btn--small"
            onClick={onConnect}
            disabled={connecting}
          >
            {connecting ? "Connecting…" : connected ? "Reconnect" : "Connect"}
          </button>
        </div>
      </label>
    </div>
  );
}

/**
 * Per-account default listing description, pre-filled on new listings for this
 * marketplace account. One editor per Chrono24/eBay account — a store can have
 * several regional Chrono24 accounts, each with its own description.
 */
function ListingDescriptionEditor({ account, onSaved }) {
  const alert = useAlert();
  const { showApiError } = useApiError();
  const [desc, setDesc] = useState(account.listing_description || "");
  const [saving, setSaving] = useState(false);

  const onSave = async () => {
    setSaving(true);
    try {
      await axios.put(`/api/marketplace-accounts/${account.id}`, { listing_description: desc });
      alert.show("Default description saved.");
      onSaved?.();
    } catch (err) {
      showApiError(err, "save the description");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ma-wf-creds">
      <label>
        <div className="ma-eb-card__label">Default listing description</div>
        <textarea
          rows={4}
          value={desc}
          placeholder="Pre-filled on new listings for this account…"
          onChange={(e) => setDesc(e.target.value)}
        />
      </label>
      <div className="ma-wf-creds__key">
        <button type="button" className="rc-btn rc-btn--small" onClick={onSave} disabled={saving}>
          {saving ? "Saving…" : "Save description"}
        </button>
      </div>
    </div>
  );
}

/**
 * Chrono24 live-publish status: the link that makes it possible.
 *
 * Chrono24 pulls our feed on its own schedule and tells us nothing, so the only
 * way to know a listing is really live is to look at this account's public
 * dealer inventory. To find that inventory we need the merchant's Chrono24
 * dealer id — and the reliable way to get it is a link to one of their OWN
 * listings. (A dealer name cannot be resolved to the right page, and merchants
 * generally cannot find their dealer page.)
 *
 * OPTIONAL, and the copy says so. The feed works without it; the only thing
 * missing is live status. It is per ACCOUNT, not per store: CH24 UK and CH24
 * Europe are different dealer pages with different inventories.
 *
 * Saving resolves the dealer immediately so the merchant finds out at once
 * whether their link worked, rather than discovering it hours later from a
 * toggle that never turned green.
 */
function Chrono24PublishLink({ account, onSaved }) {
  const alert = useAlert();
  const { showApiError } = useApiError();
  const [url, setUrl] = useState(account.ch24_seed_listing_url || "");
  const [saving, setSaving] = useState(false);

  const onSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`/api/marketplace-accounts/${account.id}`, {
        ch24_seed_listing_url: url.trim() || null,
      });

      // The endpoint reports the resolution outcome alongside the saved
      // account. A failed lookup is NOT an error response — the link is stored
      // either way and a scheduled command retries — so surface the reason
      // rather than a generic failure.
      const outcome = data?.ch24_resolution;
      if (outcome?.cleared) {
        alert.show("Publish status turned off for this account.");
      } else if (outcome && outcome.ok === false) {
        alert.show(outcome.reason || "Saved, but we could not read that listing yet.");
      } else {
        alert.show(
          outcome?.dealer_name
            ? `Connected to ${outcome.dealer_name} on Chrono24.`
            : "Chrono24 listing link saved."
        );
      }
      onSaved?.();
    } catch (err) {
      showApiError(err, "save the link");
    } finally {
      setSaving(false);
    }
  };

  const resolved = !!account.ch24_customer_id;

  return (
    <div className="ma-wf-creds">
      <label>
        <div className="ma-eb-card__label">
          Chrono24 listing link <span className="ma-ch24-link__optional">(optional)</span>
        </div>
        <input
          type="url"
          value={url}
          placeholder="https://www.chrono24.co.uk/rolex/…--id47114402.htm"
          onChange={(e) => setUrl(e.target.value)}
        />
      </label>

      <p className="ma-ch24-link__hint">
        Paste a link to any one of your own live listings on this Chrono24
        account. We use it to find your dealer page, so listing toggles can show
        when a watch is actually live on Chrono24. Leave blank to skip — your
        feed still works, you just will not see live status.
      </p>

      {resolved ? (
        <p className="ma-ch24-link__status ma-ch24-link__status--ok">
          Connected to {account.ch24_dealer_name || "your Chrono24 dealer page"}
          {account.ch24_last_sweep_at ? " · checked every 6 hours" : ""}
        </p>
      ) : (
        <p className="ma-ch24-link__status">
          Not connected — listing toggles will not show live Chrono24 status.
        </p>
      )}

      <div className="ma-wf-creds__key">
        <button type="button" className="rc-btn rc-btn--small" onClick={onSave} disabled={saving}>
          {saving ? "Checking…" : resolved ? "Update link" : "Connect"}
        </button>
      </div>
    </div>
  );
}

/**
 * eBay seller-account connection for one account. Auth is OAuth: the user
 * signs into THEIR eBay account and approves access — no keys to paste.
 * The refresh token is stored server-side (encrypted, write-only). The
 * remaining fields are the seller-account specifics eBay needs to publish.
 */
function EbayConnect({ account, onChanged }) {
  const alert = useAlert();
  const { showApiError } = useApiError();
  const [connecting, setConnecting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fields, setFields] = useState({
    merchant_location_key: account.merchant_location_key || "",
    fulfillment_policy_id: account.fulfillment_policy_id || "",
    payment_policy_id: account.payment_policy_id || "",
    return_policy_id: account.return_policy_id || "",
  });

  // status alone: DAO's legacy account is connected via the platform env
  // token, so it has no per-account key. Tenant accounts only reach
  // "connected" through the OAuth callback, which always stores a token.
  const connected = account.connection_status === "connected";
  const comingSoon = useEntitlements().featureState("sync_ebay") === "coming_soon";

  const onSignIn = async () => {
    setConnecting(true);
    try {
      const { data } = await axios.get(`/api/marketplace-accounts/${account.id}/ebay/consent-url`);
      window.open(data.url, "_blank", "noopener");
    } catch (err) {
      showApiError(err, "start the eBay sign-in");
    } finally {
      setConnecting(false);
    }
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await axios.put(`/api/marketplace-accounts/${account.id}`, fields);
      alert.show("eBay details saved.");
      onChanged?.();
    } catch (err) {
      showApiError(err, "save the eBay details");
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, placeholder) => (
    <label key={key}>
      <div className="ma-eb-card__label">{label}</div>
      <input
        type="text"
        value={fields[key]}
        placeholder={placeholder}
        onChange={(e) => setFields((f) => ({ ...f, [key]: e.target.value }))}
      />
    </label>
  );

  return (
    <div className="ma-wf-creds">
      <div className="ma-wf-creds__status">
        {connected ? (
          <span className="ma-wf-creds__ok">✓ Connected — eBay seller account linked</span>
        ) : (
          <span className="ma-wf-creds__off">Not connected</span>
        )}
      </div>

      {comingSoon ? (
        // DAO Watches only for now (Alex, 2026-10-27) — the server refuses the
        // sign-in, so don't offer it.
        <div className="ma-section__hint">eBay sync is coming soon.</div>
      ) : (
        <>
          <div className="ma-section__hint">
            <strong>What you need:</strong> an active eBay Seller account with Business
            Policies enabled.
            <br />1. Click <strong>Sign in with eBay</strong> — sign into your eBay account and approve access.
            <br />2. After approving, come back here and click <strong>Refresh</strong>.
            <br />3. Enter your policy IDs — Seller Hub → Account → Business Policies (each policy&apos;s ID is in its URL).
            <br />4. Enter your inventory location key — Seller Hub → Shipping/Locations (create one if you have none).
          </div>

          <div className="ma-wf-creds__key">
            <button type="button" className="rc-btn rc-btn--small" onClick={onSignIn} disabled={connecting}>
              {connecting ? "Opening…" : connected ? "Re-connect with eBay" : "Sign in with eBay"}
            </button>
            <button type="button" className="rc-btn rc-btn--small" onClick={() => onChanged?.()}>
              Refresh
            </button>
          </div>
        </>
      )}

      <div className="ma-eb-card__form-grid">
        {field("merchant_location_key", "Merchant Location Key", "e.g. main-warehouse")}
        {field("fulfillment_policy_id", "Fulfillment Policy ID", "e.g. 6208xxxxxx")}
        {field("payment_policy_id", "Payment Policy ID", "e.g. 6208xxxxxx")}
        {field("return_policy_id", "Return Policy ID", "e.g. 6208xxxxxx")}
      </div>
      <div className="ma-wf-creds__key">
        <button type="button" className="rc-btn rc-btn--small" onClick={onSave} disabled={saving}>
          {saving ? "Saving…" : "Save eBay details"}
        </button>
      </div>
    </div>
  );
}

/**
 * Master Admin → Marketplace Accounts.
 *
 * Lists Chrono24 (and later eBay / Watchfinder) accounts for the current
 * merchant's stores. Backend scopes by stores.owner_id == user.id, so DAO
 * admins see DAO; future B2B merchants see only their own stores.
 *
 * Endpoints (backend PR retail-circle.com #1627):
 *   GET    /api/stores/{store}/marketplace-accounts
 *   POST   /api/stores/{store}/marketplace-accounts
 *   PUT    /api/marketplace-accounts/{account}
 *   DELETE /api/marketplace-accounts/{account}
 */
export function MarketplaceAccountsPanel({ user }) {
  const alert = useAlert();
  const { showApiError } = useApiError();
  const queryClient = useQueryClient();
  const { activeStoreId } = useContext(UserContext);

  // The ACTIVE store (Phase 5 store-switcher), not the user's home store —
  // a multi-store owner acting in a store other than their home one must
  // manage THAT store's marketplace accounts. Falls back to store_id_m for
  // a user who hasn't switched; if that's also unset, the accounts hook
  // simply skips the fetch (enabled: !!storeIdM) rather than defaulting to
  // the DAO store.
  const storeIdM = activeStoreId ?? user?.store_id_m;
  const { accounts, status, refetch } = useMarketplaceAccounts(storeIdM);

  // Plan gating (Features 2+3): a second ENABLED Chrono24 account is a Black
  // feature. The add form stays usable for everything else; only this one
  // combination shows the locked notice instead.
  const { featureState } = useEntitlements();
  const enabledChronoCount = accounts.filter((a) => a.marketplace === "chrono24" && a.is_enabled).length;
  const chronoAddLocked =
    featureState("sync_chrono24") === "locked" ||
    (enabledChronoCount >= 1 && featureState("sync_chrono24_multistore") !== "on");

  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [showAddKey, setShowAddKey] = useState(false);
  const isWf = form.marketplace === "watchfinder";

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["marketplace-accounts", storeIdM] });
    refetch();
  };

  const onCreate = async () => {
    // WatchFinder is one shop per store — identity fields are fixed and the
    // user only supplies credentials. The shop's real currency is captured by
    // the connection test and overwrites this default.
    const label    = isWf ? "WatchFinder" : form.label;
    const slug     = isWf ? "wf" : form.slug;
    const currency = isWf ? "GBP" : form.currency;

    if (!isWf && (!label || !slug || !currency)) {
      alert.show("Label, slug, and currency are required.");
      return;
    }
    if (isWf && (!form.base_url || !form.api_key)) {
      alert.show("WatchFinder needs a Base URL and API key.");
      return;
    }
    const dup = accounts.find((a) => a.marketplace === form.marketplace && a.slug === slug);
    if (dup) {
      alert.show(
        isWf
          ? "A WatchFinder account already exists for this store."
          : `Slug "${slug}" is already used by ${dup.label}. Each ${form.marketplace} account needs a unique slug.`
      );
      return;
    }
    // Capture creds before the form is reset.
    const creds = { base_url: form.base_url, api_key: form.api_key };
    setCreating(true);
    try {
      const res = await axios.post(`/api/stores/${storeIdM}/marketplace-accounts`, {
        marketplace: form.marketplace,
        label,
        slug,
        currency,
        is_enabled: form.is_enabled,
      });
      setForm(EMPTY_FORM);
      setShowAddKey(false);

      // WatchFinder: test + store the credentials right after creating the row.
      // A connection failure still leaves the account created — connect it later
      // from its card.
      if (isWf) {
        try {
          await axios.post(`/api/marketplace-accounts/${res.data.id}/connect`, creds);
        } catch (connErr) {
          showApiError(connErr, "test the new account's connection");
        }
      }
      invalidate();
    } catch (err) {
      showApiError(err, "create the account");
    } finally {
      setCreating(false);
    }
  };

  const onToggleEnabled = async (account) => {
    try {
      await axios.put(`/api/marketplace-accounts/${account.id}`, {
        is_enabled: !account.is_enabled,
      });
      invalidate();
    } catch (err) {
      showApiError(err, "update the account");
    }
  };

  const onDelete = async (account) => {
    if (account.slug === null) {
      alert.show("The legacy default account can't be deleted — disable it instead.");
      return;
    }
    const url = feedUrlFor(account);
    const msg = url
      ? `Delete ${account.label}? This will remove the feed URL ${url}.`
      : `Delete ${account.label}? This removes the account and its stored credentials.`;
    if (!confirm(msg)) return;
    try {
      await axios.delete(`/api/marketplace-accounts/${account.id}`);
      invalidate();
    } catch (err) {
      showApiError(err, "delete the account");
    }
  };

  return (
    <>
      <p className="ma-section__hint">
        One row per regional Chrono24 (or eBay / Watchfinder) account.
        Disabled accounts don't appear as toggles on listing cards and their
        feed returns an empty <code>&lt;articles/&gt;</code>.
      </p>

      {status === "loading" && <Loading type="small" />}

      {status === "success" && accounts.length === 0 && (
        <p className="ma-section__empty">No accounts yet. Add one below to start.</p>
      )}

      {status === "success" && accounts.length > 0 && (
        <div className="ma-mp-list">
          {accounts.map((a) => (
            <div key={a.id} className="ma-mp-card">
              <div className="ma-mp-card__head">
                <div className="ma-mp-card__ident">
                  <div className="ma-mp-card__name">{a.label}</div>
                  <div className="ma-mp-card__meta">
                    <code>{a.marketplace}</code>
                    <span>· {a.currency}</span>
                    <span>·</span>
                    <code className="ma-mp-card__feed">{feedUrlFor(a) || a.slug}</code>
                  </div>
                  {/* The feed URL on its own looks like something the merchant
                      has to go and do. They don't — we hand it to Chrono24. */}
                  {a.marketplace === 'chrono24' && (
                    <div className="ma-mp-card__note">
                      This is your stock feed. Retail Circle will contact Chrono24 to connect it to
                      your account — you don’t need to send it anywhere yourself.
                    </div>
                  )}
                </div>
                <div className="ma-mp-card__actions">
                  <button
                    type="button"
                    className={`rc-btn rc-btn--small${a.is_enabled ? " rc-btn--on" : ""}`}
                    onClick={() => onToggleEnabled(a)}
                  >
                    {a.is_enabled ? "On" : "Off"}
                  </button>
                  {a.slug !== null && (
                    <button
                      type="button"
                      className="rc-btn rc-btn--small"
                      onClick={() => onDelete(a)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
              {a.marketplace === "watchfinder" && (
                <WatchFinderCreds account={a} onConnected={invalidate} />
              )}
              {a.marketplace === "ebay" && (
                <EbayConnect account={a} onChanged={invalidate} />
              )}
              {a.marketplace === "chrono24" && (
                <Chrono24PublishLink account={a} onSaved={invalidate} />
              )}
              {(a.marketplace === "chrono24" || a.marketplace === "ebay") && (
                <ListingDescriptionEditor account={a} onSaved={invalidate} />
              )}
            </div>
          ))}
        </div>
      )}

      <div className="ma-mp-add">
        <h4>Add account</h4>
        <div className="ma-eb-card__form-grid">
          <label className={isWf ? "ma-eb-card__span2" : undefined}>
            <div className="ma-eb-card__label">Marketplace</div>
            <select
              value={form.marketplace}
              onChange={(e) => setForm({ ...form, marketplace: e.target.value })}
            >
              <option value="chrono24">Chrono24</option>
              <option value="ebay">eBay</option>
              <option value="watchfinder">Watchfinder</option>
            </select>
            {form.marketplace === "watchfinder" && featureState("sync_watchfinder") === "locked" && (
              <div className="ma-eb-card__hint">
                WatchFinder sync is not included in your store&apos;s current plan.
              </div>
            )}
            {form.marketplace === "ebay" && featureState("sync_ebay") === "locked" && (
              <div className="ma-eb-card__hint">
                eBay sync is not included in your store&apos;s current plan.
              </div>
            )}
            {form.marketplace === "ebay" && featureState("sync_ebay") === "coming_soon" && (
              <div className="ma-eb-card__hint">eBay sync is coming soon.</div>
            )}
            {form.marketplace === "chrono24" && chronoAddLocked && (
              <div className="ma-eb-card__hint">
                {featureState("sync_chrono24") === "locked"
                  ? "Chrono24 sync is not included in your store's current plan."
                  : "Your store's current plan includes one Chrono24 account."}
              </div>
            )}
            {form.marketplace === "ebay" && (
              <div className="ma-eb-card__hint">
                Add the account first, then connect your eBay seller account
                from its card above (Sign in with eBay).
              </div>
            )}
          </label>
          {!isWf && (
            <>
              <label>
                <div className="ma-eb-card__label">Label</div>
                <input
                  type="text"
                  value={form.label}
                  placeholder="e.g. Chrono24 France"
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                />
              </label>
              <label>
                <div className="ma-eb-card__label">Slug (URL segment)</div>
                <input
                  type="text"
                  value={form.slug}
                  placeholder="e.g. fr"
                  onChange={(e) =>
                    setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })
                  }
                />
                {form.marketplace === "chrono24" && (
                  <div className="ma-eb-card__hint">
                    Feed URL will be <code>{FEED_BASE}/chrono24-feed/{form.slug || "<slug>"}</code>
                  </div>
                )}
              </label>
              <label>
                <div className="ma-eb-card__label">Currency (ISO)</div>
                <input
                  type="text"
                  value={form.currency}
                  maxLength={3}
                  onChange={(e) =>
                    setForm({ ...form, currency: e.target.value.toUpperCase().replace(/[^A-Z]/g, "") })
                  }
                />
              </label>
            </>
          )}
          {isWf && (
            <>
              <label>
                <div className="ma-eb-card__label">Base URL</div>
                <input
                  type="text"
                  name="wf_base_url"
                  value={form.base_url}
                  autoComplete="off"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  placeholder="https://watchfinder.mirakl.net"
                  onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                />
              </label>
              <label>
                <div className="ma-eb-card__label">API Key</div>
                <div className="ma-wf-creds__key">
                  <input
                    type={showAddKey ? "text" : "password"}
                    name="wf_api_key"
                    value={form.api_key}
                    autoComplete="new-password"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    placeholder="Mirakl API key"
                    onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                  />
                  <button
                    type="button"
                    className="rc-btn rc-btn--small"
                    onClick={() => setShowAddKey((s) => !s)}
                  >
                    {showAddKey ? "Hide" : "Show"}
                  </button>
                </div>
                <div className="ma-eb-card__hint">
                  Tested against the merchant's Mirakl shop when you add the account.
                </div>
              </label>
            </>
          )}
          <div className="ma-eb-card__span2 ma-mp-add__footer">
            {/* WatchFinder goes live automatically once the connection test
                passes, so the manual "go live" toggle is hidden for it. */}
            {!isWf ? (
              <label className="ma-mp-add__checkbox">
                <input
                  type="checkbox"
                  checked={form.is_enabled}
                  onChange={(e) => setForm({ ...form, is_enabled: e.target.checked })}
                />
                <span>
                  <strong>Go live immediately</strong>
                </span>
              </label>
            ) : (
              <span />
            )}
            <button
              type="button"
              className="rc-btn rc-btn--small"
              onClick={onCreate}
              disabled={creating}
            >
              {creating ? "Adding…" : isWf ? "Add & connect" : "Add account"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
